import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Search, Loader2, PlusCircle, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import MyPortfolioTab from '@/components/MyPortfolioTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Round P158·2 — Recent Tickers shortcut. Last few picked tickers are
// remembered per-browser (localStorage) and shown as one-tap chips so
// repeat buys skip the search entirely.
const RECENT_KEY = 'dividrip_quick_add_recent';
const RECENT_MAX = 6;

function getRecentTickers() {
  try {
    const arr = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    return Array.isArray(arr) ? arr.filter((t) => typeof t === 'string').slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}

function pushRecentTicker(ticker) {
  const t = (ticker || '').toUpperCase();
  if (!t) return getRecentTickers();
  const next = [t, ...getRecentTickers().filter((x) => x !== t)].slice(0, RECENT_MAX);
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* storage blocked */ }
  return next;
}

// Round P158 — Quick Stock Add. Portfolio-page modal that lets the
// user search any ticker (same live search as the Compare modal) and
// add a lot using the EXACT same Add Position form + endpoints as the
// stock modal's My Portfolio tab (quickMode hides the trade-preview
// card and portfolio-details sections). Lots land in the currently
// active portfolio via MyPortfolioTab's useActivePortfolioId.

function QuickSearchBox({ onPick }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const debounceRef = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!q.trim()) { setResults([]); setBusy(false); return undefined; }
    // P158·3 — Match the main stock-table search debounce (~1s). Fast
    // typers used to see stale partial-match lists where the exact
    // ticker sank to the bottom; waiting until typing pauses fixes it.
    setBusy(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/stocks/search`, {
          params: { q: q.trim(), limit: 8, dividend_filter: 'all' },
          withCredentials: true,
        });
        setResults(Array.isArray(r.data) ? r.data : []);
      } catch {
        setResults([]);
      } finally {
        setBusy(false);
      }
    }, 900);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  useEffect(() => {
    function onDocClick(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  return (
    <div
      ref={boxRef}
      className="relative w-full rounded-xl border-2 border-dashed border-border bg-muted/30 px-3 py-2.5 transition-colors focus-within:ring-2 focus-within:ring-primary/40"
      data-testid="quick-add-search"
    >
      <div className="flex items-center gap-2">
        <Search className="w-4 h-4 text-muted-foreground shrink-0" />
        <input
          type="text"
          value={q}
          placeholder="Search ticker or company name…"
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          className="flex-1 min-w-0 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          data-testid="quick-add-search-input"
        />
        {busy && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
      </div>

      {open && q.trim().length > 0 && (
        <div
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-72 overflow-y-auto rounded-lg border bg-popover shadow-lg"
          data-testid="quick-add-search-results"
        >
          {results.length === 0 && !busy && (
            <div className="px-3 py-3 text-xs text-muted-foreground">No matches.</div>
          )}
          {results.map((s) => (
            <button
              key={s.ticker}
              type="button"
              onClick={() => { setOpen(false); setQ(''); onPick(s.ticker); }}
              className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-muted transition-colors border-b border-border/40 last:border-b-0"
              data-testid={`quick-add-search-result-${s.ticker}`}
            >
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-bold font-mono">{s.ticker}</span>
                <span className="truncate text-[11px] text-muted-foreground max-w-[200px] sm:max-w-[280px]">{s.name}</span>
              </div>
              <span className="shrink-0 text-sm font-bold font-mono text-emerald-600 dark:text-emerald-400">
                {s.dividend_yield ? `${s.dividend_yield.toFixed(2)}%` : '—'}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function QuickStockAddButton({ onPositionsChanged }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [stock, setStock] = useState(null);
  const [loadingStock, setLoadingStock] = useState(false);
  const [added, setAdded] = useState(false);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    if (modalOpen) setRecent(getRecentTickers());
  }, [modalOpen]);

  const pickTicker = async (ticker) => {
    setLoadingStock(true);
    setAdded(false);
    try {
      const r = await axios.get(`${API}/stocks/${encodeURIComponent(ticker)}`, { withCredentials: true });
      setStock(r.data);
      setRecent(pushRecentTicker(r.data?.ticker || ticker));
    } catch {
      setStock(null);
    } finally {
      setLoadingStock(false);
    }
  };

  const close = (open) => {
    setModalOpen(open);
    if (!open) { setStock(null); setAdded(false); }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setModalOpen(true)}
        // P158·3 — Make the button pop off the portfolio page.
        // Thicker emerald border + soft tint so users spot it on
        // first glance, matching the emerald + PlusCircle icon.
        className="gap-1.5 border-2 border-emerald-500/70 bg-emerald-50/60 hover:bg-emerald-100 dark:bg-emerald-950/30 dark:hover:bg-emerald-950/50 hover:border-emerald-500 text-emerald-800 dark:text-emerald-200 font-semibold shadow-sm"
        data-testid="quick-stock-add-btn"
        title="Quickly add a stock lot to this portfolio — search any ticker and enter your shares without opening the full stock modal."
      >
        <PlusCircle className="w-[17px] h-[17px] text-emerald-600 dark:text-emerald-400" />
        Quick Stock Add
      </Button>

      <Dialog open={modalOpen} onOpenChange={close}>
        <DialogContent
          // P158·3 — Kill the stray horizontal scrollbar and clamp
          // to viewport width on phones. `overflow-x-hidden` keeps
          // any long name/company strings from spilling out; the
          // width formula matches the shared dialog padding.
          className="w-[calc(100vw-1.5rem)] max-w-lg max-h-[85vh] overflow-y-auto overflow-x-hidden p-4 sm:p-6"
          // Clicking the sonner toast ("Journal it" / "Maybe later") must not
          // count as an outside click — closing the dialog would unmount the
          // journal modal before it opens.
          onInteractOutside={(e) => { if (e.target?.closest?.('[data-sonner-toaster]')) e.preventDefault(); }}
          data-testid="quick-stock-add-modal"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PlusCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              Quick Stock Add
            </DialogTitle>
            <DialogDescription className="break-words">
              Quickly add a stock to this portfolio — search for your stock below, then
              enter the lot exactly like the stock modal&apos;s My Portfolio tab. Lots are
              added to the portfolio you&apos;re currently viewing.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <QuickSearchBox onPick={pickTicker} />

            {/* One-tap repeat buys — last few tickers picked here */}
            {recent.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap" data-testid="quick-add-recent-tickers">
                <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Recent:</span>
                {recent.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => pickTicker(t)}
                    className={`px-2 py-0.5 rounded-full border text-[11px] font-mono font-bold transition-colors ${
                      stock?.ticker === t
                        ? 'bg-emerald-500/15 border-emerald-500/50 text-emerald-700 dark:text-emerald-400'
                        : 'bg-muted/40 border-border text-foreground/80 hover:bg-muted hover:text-foreground'
                    }`}
                    title={`Quick-select ${t} again`}
                    data-testid={`quick-add-recent-${t}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}

            {loadingStock && (
              <div className="flex items-center justify-center py-6 text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            )}

            {!loadingStock && stock?.ticker && (
              <>
                {/* Chosen stock — Current Price ONLY per spec.
                    P158·3: Stack ticker/name and price on separate
                    rows so long company names never collide with the
                    price and the clear-button always stays reachable. */}
                <div className="rounded-lg border bg-card px-3 py-2 space-y-1" data-testid="quick-add-chosen-stock">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <span className="font-mono text-sm font-bold">{stock.ticker}</span>
                      <span className="ml-2 text-xs text-muted-foreground break-words">{stock.name}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setStock(null)}
                      className="shrink-0 p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                      title="Clear and pick a different stock"
                      data-testid="quick-add-clear-stock"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="text-sm font-mono font-semibold" data-testid="quick-add-current-price">
                    Current Price: ${Number(stock.price || 0).toFixed(2)}
                  </div>
                </div>

                {/* The REAL My Portfolio add form (quickMode hides the
                    trade-preview card + portfolio-details sections) so
                    every field and endpoint behaves identically. */}
                <MyPortfolioTab
                  key={stock.ticker}
                  stock={stock}
                  quickMode
                  onPositionsChanged={() => {
                    setAdded(true);
                    onPositionsChanged?.();
                  }}
                />

                {added && (
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold text-center" data-testid="quick-add-success-note">
                    Added to this portfolio ✓ — search another ticker above or close this window.
                  </p>
                )}
              </>
            )}

            {!loadingStock && !stock && (
              <p className="text-xs text-muted-foreground text-center py-2" data-testid="quick-add-empty-hint">
                Search a ticker above to get started.
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
